import { useCallback, useEffect, useRef, useState } from "react";
import GraphHeader from "../../CommonComponents/GraphHeader";
import CommonLineGraph from "../../CommonComponents/CommonLineGraph";
import { useDispatch, useSelector } from "react-redux";
import { OdInterestSavedThunk } from "../../../../../redux/features/Settings/Statistics";
import formatTotalBalance from "../../../../../utils/formatTotalBalance";
import FecthingPage from "../../CommonComponents/FecthingPage";
import CommonToast from "../../../../_utils/CommonToast";
import ICONS from "../../../../../assets/Icons";
import {
  errorcode,
  GlobalConfiguration,
  SettingsStatisticsConfiguration,
} from "../../../../../utils/configurationProvider";
import globalText from "../../../../../utils/globalText";
import dayjs from "dayjs";

const OdInterestSaved = ({
  flow,
  autoFetch = false,
  toggleAddedToHome,
  isAddedToHome,
}) => {
  const DEFAULT_GRAPH_DATA = {
    // NOTE: This is default dummy data for graph - used as fallback before fetch
    defaultItemsSizeForXAxis: [0, 1, 2, 3, 4, 5, 6],
    dataFirst: [10, 50, 80, 70, 30, 20, 100],
    dataSecond: [0, 0, 0, 0, 0, 0, 0],
    xAxisData: [
      "2025-01-01",
      "2025-01-02",
      "2025-01-03",
      "2025-01-04",
      "2025-01-05",
      "2025-01-06",
      "2025-01-07",
    ],
    OdTotalAmount: 5600000,
    dailyInterest: 7, // default intrest
  };
  const [graphData, setGraphData] = useState(DEFAULT_GRAPH_DATA);

  const [selectedDays, setSelectedDays] = useState(
    globalText?.LAST_7_DAYS.toLowerCase(),
  );
  const [selectedAccount, setSelectedAccount] = useState([]);
  const [fetchPageEnabled, setFetchPageEnabled] = useState(!autoFetch);
  const [isError, setIsError] = useState(false);
  const [fetchPageLoading, setFetchPagefetchPageLoading] = useState(false);
  const toastShownRef = useRef(false);
  const services = useSelector((state) => state.refreshToken.services);
  const globalStatisticsService = services?.includes("VStatistics"); // Global service for statistics.

  const dispatch = useDispatch();

  const loginData = useSelector((state) => state.refreshToken.data);
  const ownerAccLoading = useSelector(
    (state) => state.ownerAllVerification.ownerViewAllListAccLoading,
  );

  const { OdInterestSavedLoading, OdInterestSavedError } = useSelector(
    (state) => state.settingStatistics,
  );

  /**
   * IMPORTANT: Below state `isInitialAutoFetching`:
   * Tracks the auto-fetch lifecycle when the component mounts with autoFetch enabled.
   *
   * Purpose:
   * 1. Eliminates loader flickering between the time owner accounts finish fetching
   *    in GraphHeader and the subsequent dispatch of OdInterestSavedThunk.
   * 2. Prevents the default/dummy graph from flashing on initial mount.
   * 3. Initialized to `autoFetch` so that manual-fetch mode (autoFetch = false)
   *    displays the "Fetch" action button instead of getting stuck in a loading state.
   */
  const [isInitialAutoFetching, setIsInitialAutoFetching] = useState(autoFetch);

  const entityId = loginData?.status ? loginData?.entity?.entity_id : "";

  /* Helper to check if valid accounts are loaded */
  const hasValidAccounts = Array.isArray(selectedAccount)
    ? selectedAccount.length > GlobalConfiguration?.lengthCheckZero
    : Boolean(selectedAccount);

  /* Helper to transform API response into graph-friendly arrays */
  const transformReportData = (reports) => {
    return Object.entries(reports).reduce(
      (acc, [day, value], index) => {
        acc.defaultGraphDataItemsForXaxis.push(index);
        acc.graphDataFirst.push(value);
        acc.graphDataSecond.push(0.0); // placeholder values
        acc.xAxisLabels.push(day);
        return acc;
      },
      {
        defaultGraphDataItemsForXaxis: [],
        graphDataFirst: [],
        graphDataSecond: [],
        xAxisLabels: [],
      },
    );
  };

  const formatDate = (str) => {
    const [dd, mm, yyyy] = str.trim().split("-");
    return `${yyyy}-${mm}-${dd}`;
  };

  /* Fetch and update state with API data */
  const handleCalculation = useCallback(
    async (response) => {
      const reportData = response?.payload?.report_data;

      if (response?.payload?.status && reportData) {
        let {
          defaultGraphDataItemsForXaxis,
          graphDataFirst,
          graphDataSecond,
          xAxisLabels,
        } = transformReportData(reportData.daily_breakdown || []);

        xAxisLabels = xAxisLabels.map((val, index) => {
          return formatDate(val);
        });

        setGraphData({
          defaultItemsSizeForXAxis: defaultGraphDataItemsForXaxis,
          dataFirst: graphDataFirst,
          dataSecond: graphDataSecond,
          xAxisData: xAxisLabels,
          OdTotalAmount: reportData.od_total_amount,
          dailyInterest: reportData.interest_rate,
        });
        // toast shows only once
        if (!toastShownRef.current && fetchPageEnabled) {
          CommonToast({
            text: "Fetched successfully.",
            icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
            variant: "var(--success)",
            btnProps: {
              isDisabled: false,
              enabledTextColor: "var(--text-primary)",
              enabledBgColor: "var(--primary-bg)",
            },
          });
          toastShownRef.current = true;
        }
        setFetchPageEnabled(false);
        setIsError(false);
      } else {
        // fallback to defaults
        setGraphData(DEFAULT_GRAPH_DATA);
        setFetchPageEnabled(
          response?.payload?.status !== errorcode?.errorNotFound,
        );
        toastShownRef.current = false;

        setIsError(true);
      }
    },
    [dispatch, selectedAccount, selectedDays],
  );

  /* Fetch and update state with API data */
  const fetchData = async () => {
    if (!hasValidAccounts || !entityId) return;
    setIsError(false);
    setIsInitialAutoFetching(true);

    const response = await dispatch(
      OdInterestSavedThunk({
        owner_acct_ids: selectedAccount,
        period: selectedDays,
        entity_id: entityId,
      }),
    );

    setIsInitialAutoFetching(ownerAccLoading || OdInterestSavedLoading);
    setFetchPagefetchPageLoading(false);
    handleCalculation(response);
  };

  /* Button handler */
  const handleFetchClick = () => {
    setFetchPagefetchPageLoading(true);
    setFetchPageEnabled(false);
  };

  /*Auto-fetch on dependency change */
  useEffect(() => {
    if (!fetchPageEnabled || autoFetch) {
      fetchData();
    }
  }, [autoFetch, fetchPageEnabled, selectedDays, selectedAccount]);

  return (
    <div
      className={`w-width-full flex flex-col bg-primary-card ${flow === "homeDashboard" ? "rounded-b-border-radius-md" : "rounded-border-radius-md"}`}
    >
      <GraphHeader
        handlingOptionsRequired={[
          { type: "days", setterState: setSelectedDays },
          { type: "allAccounts", setterState: setSelectedAccount },
        ]}
        titleText={"OD interest saved"}
        flow={flow}
        autoFetch={autoFetch}
        hideTitle={flow === "homeDashboard" ? true : false}
        isAddedToHome={isAddedToHome}
        toggleAddedToHome={toggleAddedToHome}
      />
      <div className="flex flex-col relative">
        <FecthingPage
          errorData={
            OdInterestSavedError &&
            OdInterestSavedError === errorcode?.errorNotFound
              ? { status: errorcode?.errorNotFound }
              : null
          }
          fetchPageEnabled={
            (!autoFetch && fetchPageEnabled) || !globalStatisticsService
          }
          loading={
            isInitialAutoFetching ||
            OdInterestSavedLoading ||
            fetchPageLoading ||
            (ownerAccLoading && autoFetch)
          }
          isError={isError ? OdInterestSavedError : null}
          handleFunction={handleFetchClick}
        />

        <div className="w-width-full flex Qsmd:flex-row flex-col-reverse">
          <div className="Qsmd:w-auto w-width-full h-auto flex-col bg-primary-nav justify-center items-center flex-shrink-0 overflow-y-auto Qsmd:rounded-bl-border-radius-md Qsmd:rounded-none rounded-b-border-radius-md">
            <div className="text-text-primary text-font-sm Qsmd:text-font-xxs font-semibold mb-margin-md Qsmd:pt-padding-sm pt-padding-md px-padding-md">
              {`Interest charged (${graphData.dailyInterest}%) day wise`}
            </div>

            <div className="max-h-[var(--statistics-left-container-height)] min-h-[var(--statistics-left-container-height)] px-padding-md overflow-y-scroll">
              <div
                className="flex flex-col gap-gap-sm w-width-full Qsmd:h-height-full h-auto Qsmd:pb-padding-base Qsmd:overflow-y-auto overflow-visible"
                style={{ scrollbarWidth: "none" }}
              >
                {graphData.dataFirst.map((val, index) => {
                  return (
                    <div
                      className="flex justify-between"
                      key={index}
                    >
                      <div className="text-text-secondary Qsmd:text-font-xxs text-font-xs">
                        {/* Format date as "DD MMM" (e.g., "01 Jan") */}
                        {dayjs(graphData.xAxisData[index]).format("DD MMM")}
                      </div>
                      <div className="text-text-secondary Qsmd:text-font-xxs text-font-xs font-semibold">
                        {fetchPageEnabled
                          ? "-"
                          : "INR " + formatTotalBalance(val, "amount").amount}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="border-b-border border-b-primary-bg mx-margin-md Qsmd:mt-margin-md mt-margin-button-l "></div>
            <div className="w-width-full h-auto flex justify-between Qsmd:mt-margin-md Qsmd:mb-bottom-base my-margin-button-l px-padding-md">
              <div className="text-text-secondary text-font-xs Qsmd:text-font-xxs">
                Total
              </div>
              <div className="text-negative-act text-font-xs Qsmd:text-font-xxs font-semibold">
                {fetchPageEnabled
                  ? "-"
                  : "INR " +
                    formatTotalBalance(graphData.OdTotalAmount, "amount")
                      .amount}
              </div>
            </div>
            <div className="Qsmd:hidden w-width-full px-padding-md Qsmd:pb-padding-sm pb-padding-base">
              <div className="w-width-full h-auto flex justify-between Qsmd:py-padding-sm py-padding-md border-t-border border-t-primary-bg">
                <div className="text-text-secondary text-font-xs Qsmd:text-font-xxs">
                  Interest charged with Xpentra
                </div>
                <div className="text-positive-act text-font-xs Qsmd:text-font-xxs font-semibold">
                  INR 00.00
                </div>
              </div>
            </div>
          </div>

          {/* Right Section: Line Graph & Desktop Footer */}
          <div className="w-width-full min-w-0 flex flex-col justify-between">
            {/* Line Graph Container with horizontal scrolling for many data points */}
            <div className="w-width-full pb-padding-sm relative overflow-x-auto">
              {/* Dynamic minimum width based on data points count for proper spacing */}
              <div
                style={{
                  minWidth: `${Math.max(
                    (graphData.xAxisData?.length ||
                      GlobalConfiguration?.lengthCheckZero) *
                      SettingsStatisticsConfiguration?.graphMinWidthPerDataPoint,
                  )}px`,
                }}
              >
                <CommonLineGraph
                  graphDataFirst={graphData.dataFirst}
                  graphDataSecond={graphData.dataSecond}
                  xAxisLabels={graphData.xAxisData}
                  graphDataFirstColor={"var(--failed)"}
                  graphDataSecondColor={"var(--settled)"}
                  defaultDataItemsForXaxis={graphData.defaultItemsSizeForXAxis}
                />
              </div>
            </div>

            {/* Desktop Footer (placed below the scrollbar) */}
            <div className="hidden Qsmd:block py-padding-sm rounded-b-border-radius-md Qsmd:rounded-bl-none bg-primary-nav">
              <div className="hidden Qsmd:flex w-width-full h-auto bg-primary-nav border-l-border border-l-primary-bg pl-padding-sm Qsmd:rounded-br-border-radius-md Qsmd:rounded-bl-none rounded-b-border-radius-md leading-[var(--font-md)]">
                <div className="text-text-secondary text-font-xxs">
                  Interest charged with Xpentra : {"  "}
                </div>
                <div className="text-positive-act text-font-xxs pl-padding-xs font-semibold">
                  {" "}
                  INR 00.00
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OdInterestSaved;
